import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useGetCollectionMembers } from '@/api/trafikportalen';
import type { IssuedTicketFindResultDTO } from '@/api/trafikportalen';
import { isStudentCollectionType } from '@/helpers/collectionTypes';
import { formatDate } from '@/helpers/formatting';
import { getMembershipStatus } from '@/helpers/membershipStatus';
import { ticketStatusDeadline } from '@/helpers/ticket';
import {
  issuedTicketRevokedBecauseStudentWillInactivate,
  ticketRevocationMessages,
} from '@/helpers/ticketRevocation';
import { useTicketStatusOptions } from '@/hooks/useTicketTranslations';
import { useTicketBobcatData } from '@/stores/ticketBobcatData';

const MEMBER_LOOKUP_STALE_TIME = 1000 * 60 * 5;

export interface DisplayedTicketStatus {
  label: string;
  description: string;
  ariaDescription?: string;
  notice?: string;
}

const useStudentInactivateAt = (
  collectionId?: string,
  collectionMemberId?: string,
  collectionType?: string,
) => {
  const canLookUp =
    !!collectionId &&
    !!collectionMemberId &&
    !!collectionType &&
    isStudentCollectionType(collectionType);

  const { data } = useGetCollectionMembers(collectionId ?? '', {
    query: {
      enabled: canLookUp,
      retry: false,
      staleTime: MEMBER_LOOKUP_STALE_TIME,
    },
  });

  return useMemo(() => {
    if (!canLookUp || !data) return undefined;
    const member = data.find(
      (entry) => entry.collection_member.id === collectionMemberId,
    );
    if (!member) return undefined;
    if (getMembershipStatus(member.collection_member) !== 'will-inactivate') {
      return undefined;
    }
    return member.collection_member.inactivate_at;
  }, [canLookUp, collectionMemberId, data]);
};

export const useDisplayedTicketStatus = (
  ticket: IssuedTicketFindResultDTO,
): DisplayedTicketStatus => {
  const { formatMessage } = useIntl();
  const { getStatusTranslationLong } = useTicketStatusOptions();
  const bobcatData = useTicketBobcatData(ticket.id);
  const deadline = ticketStatusDeadline(ticket, bobcatData);
  const inactivateAt = useStudentInactivateAt(
    ticket.collection_id,
    ticket.collection_member_id,
    ticket.collection_type,
  );

  return useMemo(() => {
    const status = getStatusTranslationLong(ticket, deadline);
    if (
      !inactivateAt ||
      !issuedTicketRevokedBecauseStudentWillInactivate(ticket, inactivateAt)
    ) {
      return status;
    }

    const date = formatDate(inactivateAt);
    const notice = formatMessage(ticketRevocationMessages.reason, { date });
    return {
      label: formatMessage(ticketRevocationMessages.label),
      description: formatMessage(ticketRevocationMessages.description, {
        date,
      }),
      ariaDescription: notice,
      notice,
    };
  }, [deadline, formatMessage, getStatusTranslationLong, inactivateAt, ticket]);
};
